import React, { useCallback, useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import DatePicker from "react-datepicker";
import CustomDatePickerInput from "../../_common/CustomDatePickerInput";
import { useDispatch, useSelector } from "react-redux";
import { QMS } from "../../../_constants/actionConstants";
import cmtIssueActions from "../../../_actions/_qmsActions/cmtIssueActions";
import cmtCategoryActions from "../../../_actions/_qmsActions/cmtCategoryActions";
import qmsTeamActions from "../../../_actions/_qmsActions/qmsTeamActions";
import cmtIssueStageActions from "../../../_actions/_qmsActions/cmtIssueStageActions";
import cmtStageActions from "../../../_actions/_qmsActions/cmtStageActions";
import { getLocalISOString } from "../../../_utils/generalUtils";
import { addDays, intervalToDuration, isAfter, isBefore, isEqual } from "date-fns";
import AlertBox from "../../_common/AlertBox";

export default function CmtModal_EditIssueStage(props) {
  const { modalState, toggleModal, issue, qmsUserId, selectStage } = props;

  const dispatch = useDispatch();

  const fetching = useSelector((state) => state.qms.fetching);

  const cmtStages = useSelector((state) => state.qms.cmtStages[issue.divisionId] || null);
  const qmsTeams = useSelector((state) => state.qms.qmsTeams[issue.divisionId] || null);

  useEffect(() => {
    dispatch(cmtStageActions.getAll(issue.divisionId));
    dispatch(qmsTeamActions.getAll(issue.divisionId));
  }, []);

  const cmtStageOptions = useMemo(
    () => [
      { value: 0, label: "Custom (Manual Entry)" },
      ...(cmtStages || []).map((cs) => ({
        value: cs.cmtStageId,
        label: cs.title,
      })),
    ],
    [cmtStages]
  );
  const qmsTeamOptions = useMemo(
    () => (qmsTeams || []).map((qt) => ({ value: qt.qmsTeamId, label: qt.name })),
    [qmsTeams]
  );

  const [selectedCmtStage, setCmtStage] = useState(
    cmtStageOptions.find((o) => o.value === 0)
  );
  const [title, setTitle] = useState((modalState.data && modalState.data.title) || "");
  const [description, setDescription] = useState(
    (modalState.data && modalState.data.description) || ""
  );
  const [startedOn, setStartedOn] = useState(
    modalState.data && modalState.data.startedOn
      ? new Date(modalState.data.startedOn)
      : modalState.data && !modalState.data.isCurrent
      ? null
      : new Date()
  );
  const [completedOn, setCompletedOn] = useState(
    modalState.data && modalState.data.completedOn
      ? new Date(modalState.data.completedOn)
      : null
  );

  const [duration, setDuration] = useState(
    (modalState.data && `${modalState.data.duration}`) || "0"
  );
  const [selectedOwnerTeam, setOwnerTeam] = useState(null); // Update later when teams load
  useEffect(() => validateForm("ownerTeam"), [selectedOwnerTeam]);

  const [formErrors, setFormErrors] = useState({
    title: null,
    ownerTeam: null,
  });

  const futureDates = useCallback((date) => !isAfter(date, new Date()), []);
  const completionDates = useCallback(
    (date) => {
      date.setHours(0, 0, 0, 0);
      return startedOn
        ? (isAfter(date, startedOn) || isEqual(date, startedOn)) &&
            isBefore(date, addDays(new Date().setHours(0, 0, 0, 0), 1))
        : futureDates(date);
    },
    [startedOn, futureDates]
  );

  useEffect(() => {
    if (modalState.data && modalState.data.ownerTeamId) {
      const qto = (qmsTeamOptions || []).find(
        (o) => o.value === modalState.data.ownerTeamId
      );
      if (qto) setOwnerTeam(qto);
    }
  }, [qmsTeamOptions]);

  // Set title/desc/duration fields based on selected CmtStage
  useEffect(() => {
    if (selectedCmtStage) {
      const cs = (cmtStages || []).find((s) => s.cmtStageId === selectedCmtStage.value);
      setTitle(cs ? cs.title || "" : title || "");
      setDescription(cs ? cs.description || "" : description || "");
      setDuration(cs ? cs.duration || "" : duration || "");

      if (qmsTeamOptions && cs) {
        if (cs.defaultOwnerTeamId) {
          const qto = qmsTeamOptions.find((o) => o.value === cs.defaultOwnerTeamId);
          setOwnerTeam(qto || null);
        } else setOwnerTeam(null);
      }
    } else setCmtStage(cmtStageOptions[0]);
  }, [selectedCmtStage]);

  // If title/desc/owner values deviate from selected preset stage's values,
  // clear preset stage
  useEffect(() => {
    if (selectedCmtStage) {
      const cs = (cmtStages || []).find((s) => s.cmtStageId === selectedCmtStage.value);
      if (cs) {
        if (title !== cs.title) setCmtStage(cmtStageOptions[0]);
        if (description !== cs.description) setCmtStage(cmtStageOptions[0]);
        if (
          (!selectedOwnerTeam && cs.defaultOwnerTeamId) ||
          (selectedOwnerTeam &&
            selectedOwnerTeam.value !== (cs.defaultOwnerTeamId || null))
        ) {
          setCmtStage(cmtStageOptions[0]);
        }
      }
    }
  }, [title, description, selectedOwnerTeam]);

  const validateForm = (fieldName) => {
    const defaultMsg = "Required";
    switch (fieldName) {
      case "title":
        setFormErrors({ ...formErrors, title: !title ? defaultMsg : null });
        break;
      case "ownerTeam":
        setFormErrors({
          ...formErrors,
          ownerTeam: !selectedOwnerTeam ? defaultMsg : null,
        });
        break;
      default:
        break;
    }
  };

  const formIsIncomplete = () =>
    Object.values(formErrors).some((e) => e) || !title || !selectedOwnerTeam;

  const handleClick_update = async (e) => {
    e.preventDefault();
    const issueStageData = {
      issueStageId: modalState.data.issueStageId,
      issueId: issue.issueId,
      cmtStageId: selectedCmtStage.value ? selectedCmtStage.value : null,
      title,
      description,
      duration: +duration,
      ownerTeamId: selectedOwnerTeam ? selectedOwnerTeam.value : null,
      startedOn: startedOn ? getLocalISOString(startedOn) : null,
      startedByQmsUserId: modalState.data.startedByQmsUserId,
      isCurrent: modalState.data.isCurrent,
      isCompleted: modalState.data.isCompleted,
      completedOn: completedOn ? getLocalISOString(completedOn) : null,
    };
    dispatch({
      type: QMS.ISSUE_STAGE.UPDATE_CLIENT,
      issueId: issue.issueId,
      fieldsToUpdate: issueStageData,
    });
    await dispatch(cmtIssueStageActions.update(issue.issueId));

    // Reload issue with updated issueStageSnippets
    dispatch(cmtIssueActions.get(issue.issueId));
    dispatch(cmtIssueActions.reloadParent(issue.parentIssueId));
    dispatch(cmtIssueActions.getAll(issue.divisionId));

    toggleModal();
  };

  const handleClick_add = async (e) => {
    e.preventDefault();
    const issueStageData = {
      issueId: issue.issueId,
      cmtStageId: selectedCmtStage.value ? selectedCmtStage.value : null,
      title,
      description,
      duration: +duration,
      ownerTeamId: selectedOwnerTeam ? selectedOwnerTeam.value : null,
      startedOn: startedOn ? getLocalISOString(startedOn) : null,
      startedByQmsUserId: qmsUserId,
      isCurrent: startedOn != null,
      completedOn: completedOn ? getLocalISOString(completedOn) : null,
    };
    const newIssueStageId = await dispatch(cmtIssueStageActions.add(issueStageData));

    // Reload issue with updated issueStageSnippets
    dispatch(cmtIssueActions.get(issue.issueId));
    dispatch(cmtIssueActions.reloadParent(issue.parentIssueId));
    dispatch(cmtIssueActions.getAll(issue.divisionId));

    selectStage(newIssueStageId);

    toggleModal();
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <Modal isOpen={modalState.showModal} toggle={toggleModal} fade={false} size={"lg"}>
      <ModalHeader toggle={toggleModal} className="bg-info text-white" close={closeBtn}>
        <FontAwesomeIcon
          icon={modalState.data ? "edit" : "plus"}
          className="fa-fw mr-2"
        />
        {modalState.data ? "Edit " : "Add New "}
        Stage
      </ModalHeader>
      <ModalBody>
        <div className="px-3">
          {modalState.data && modalState.data.isCompleted && (
            <AlertBox color="warning" icon="exclamation-circle" className="mb-2">
              You are viewing a completed stage. Stage Owner and Planned Duration fields
              can no longer be edited after completion. Date Started and Date Completed
              fields may be edited, but cannot be cleared.
            </AlertBox>
          )}
          {modalState.data &&
            !modalState.data.isCompleted &&
            !modalState.data.isCurrent && (
              <AlertBox color="warning" icon="exclamation-circle" className="mb-2">
                This stage has not started yet. Dates Started/Completed cannot be edited
                until the stage work starts.
              </AlertBox>
            )}
          <div className="row">
            <div className="form-group col-12 mb-1">
              <label htmlFor="presetSelect" className="mb-0">
                Preset Stage:
              </label>
              <Select
                name="presetSelect"
                id="presetSelect"
                value={selectedCmtStage}
                options={cmtStageOptions}
                onChange={(selected) => setCmtStage(selected)}
                isClearable
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
                isDisabled={modalState.data && modalState.data.isCompleted}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-group col-12 mb-1">
              <label htmlFor="title" className="mb-0 font-weight-bold">
                Title:*
              </label>
              <input
                id="title"
                name="title"
                className="form-control"
                maxLength="30"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={(e) => validateForm(e.target.name)}
              />
              {formErrors.title && (
                <span className="error-small">
                  <small>{formErrors.title}</small>
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-group col-12 mb-1">
              <label htmlFor="description" className="mb-0">
                Description:
              </label>
              <textarea
                id="description"
                name="description"
                className="form-control"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                onBlur={(e) => validateForm(e.target.name)}
              />
            </div>
          </div>
          <div className="row mb-2">
            <div className="form-group col-12 mb-1">
              <label htmlFor="ownerTeam" className="mb-0 font-weight-bold">
                Stage Owner:*
              </label>
              <Select
                id="ownerTeam"
                name="ownerTeam"
                className="pl-0"
                value={selectedOwnerTeam}
                options={qmsTeamOptions}
                onChange={(selected) => setOwnerTeam(selected)}
                onBlur={() => validateForm("ownerTeam")}
                isClearable
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
                isDisabled={modalState.data && modalState.data.isCompleted}
              />
              {formErrors.ownerTeam && (
                <span className="error-small">
                  <small>{formErrors.ownerTeam}</small>
                </span>
              )}
            </div>
          </div>
          <div className="row mb-0">
            <div className="form-group col-sm-4 mb-1 pr-1">
              <label
                htmlFor="startedOn"
                className={`mb-0${
                  modalState.data && modalState.data.isCompleted
                    ? " font-weight-bold"
                    : ""
                }`}
              >
                Date Started:
                {modalState.data && modalState.data.isCompleted && "*"}
              </label>
              <DatePicker
                customInput={
                  <CustomDatePickerInput
                    isClearable
                    onClear={() => setStartedOn(null)}
                    disableClear={
                      modalState.data &&
                      (modalState.data.isCompleted || !modalState.data.isCurrent)
                    }
                  />
                }
                name="startedOn"
                id="startedOn"
                selected={startedOn}
                dateFormat="MM/dd/yyyy (E)"
                onChange={(selected) => setStartedOn(selected)}
                filterDate={futureDates}
                disabled={
                  modalState.data &&
                  !modalState.data.isCurrent &&
                  !modalState.data.isCompleted
                }
              />
              {!modalState.data && (
                <div style={{ lineHeight: "0.9rem" }}>
                  <span>
                    <small>
                      Leave this field blank: 1) if unknown, or 2) to make this stage a
                      placeholder for future.
                    </small>
                  </span>
                </div>
              )}
            </div>
            <div className="form-group col-sm-4 mb-1 px-1">
              <label
                htmlFor="completedOn"
                className={`mb-0${
                  modalState.data && modalState.data.isCompleted
                    ? " font-weight-bold"
                    : ""
                }`}
              >
                Date Completed:
                {modalState.data && modalState.data.isCompleted && "*"}
              </label>
              <DatePicker
                customInput={
                  <CustomDatePickerInput
                    isClearable
                    onClear={() => setCompletedOn(null)}
                    disableClear={modalState.data}
                  />
                }
                name="completedOn"
                id="completedOn"
                selected={completedOn}
                dateFormat="MM/dd/yyyy (E)"
                onChange={(selected) => setCompletedOn(selected)}
                filterDate={completionDates}
                disabled={
                  modalState.data &&
                  (modalState.data.isCurrent || !modalState.data.isCompleted)
                }
              />
            </div>
            <div className="form-group col-sm-4 mb-1 pl-1">
              <label htmlFor="duration" className="mb-0">
                Planned Duration:
              </label>
              <div className="input-group mb-0">
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  className="form-control"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  disabled={modalState.data && modalState.data.isCompleted}
                />
                <div className="input-group-append">
                  <span className="input-group-text">Days</span>
                </div>
              </div>
              {startedOn && completedOn && (
                <div style={{ lineHeight: "0.9rem" }}>
                  <span>
                    <small>
                      Actual duration is{" "}
                      {(intervalToDuration({
                        start: new Date(new Date(startedOn).setHours(0, 0, 0, 0)),
                        end: new Date(new Date(completedOn).setHours(0, 0, 0, 0)),
                      }).days || 0) + 1}{" "}
                      day(s).
                    </small>
                  </span>
                </div>
              )}
              {startedOn && duration && +duration > 0 && (
                <div style={{ lineHeight: "0.9rem" }}>
                  <span>
                    <small>
                      Planned completion date is{" "}
                      {addDays(startedOn, duration).toLocaleDateString()}.
                    </small>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="bg-lightgray">
        <div className="form-group form-row float-right mb-0">
          <button
            type="button"
            name="open"
            id="open"
            className="btn btn-primary mr-2"
            onClick={modalState.data ? handleClick_update : handleClick_add}
            disabled={formIsIncomplete()}
          >
            {fetching && (
              <span className="mr-2 mt-1">
                <FontAwesomeIcon
                  icon="spinner"
                  className="loading-spinner"
                  style={{ color: "white" }}
                />
              </span>
            )}
            <b>{modalState.data ? "Update" : "Add"}</b>
          </button>
          <button
            type="button"
            name="cancel"
            id="cancel"
            className="btn btn-secondary"
            onClick={toggleModal}
          >
            Cancel
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
